"use client";

import { useTranslations } from "next-intl";
import { registerUser } from "@/api/user/registerUser";
import { RegisterContext } from "@/context";
import { validateAadhaar, validatePassword, validatePhoneNumber } from "@/utils/utils";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import BadgeIcon from "@mui/icons-material/Badge";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LockIcon from "@mui/icons-material/Lock";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import { Alert, Button, Card, InputAdornment, TextField, Typography } from "@mui/material";
import { useContext, useRef, useState, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Lock } from "lucide-react";
import OTPModal from "../aadhar-kyc/VerifyAadhar";

const setErrorAndScroll = ({ setError, message, topRef }) => {
  setError(message);
  topRef.current?.scrollIntoView({
    behavior: "smooth",
    block: "start",
    inline: "nearest" 
  });
};

export const BasicInfoRegister = () => {
  const t = useTranslations();
  const { activeStep, setActiveStep } = useContext(RegisterContext);
  const [aadharNumber, setAadharNumber] = useState("");
  const { mobileNumber, setMobileNumber } = useContext(RegisterContext);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const topRef = useRef(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [aadhaarModalIsOpen, setAadhaarModalIsOpen] = useState(false);
  const [isAadhaarVerified, setIsAadhaarVerified] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const {
    isLengthValid,
    hasNumber,
    hasUpperCase,
    hasLowerCase,
    hasSpecialChar,
    confirmPasswordValid
  } = validatePassword(password, confirmPassword);

  const isValidAadhaar = validateAadhaar(aadharNumber?.replace(/ /g, ""));
  const isValidPhoneNumber = validatePhoneNumber(mobileNumber);

  // Auto-hide success alert after 5 seconds
  useEffect(() => {
    if (showSuccessAlert) {
      const timer = setTimeout(() => {
        setShowSuccessAlert(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [showSuccessAlert]);

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
  };

  const handleVerifyAadhaar = () => {
    if (!isValidAadhaar) {
      setErrorAndScroll({ setError, message: "Please enter a valid Aadhaar number first.", topRef });
      return;
    }
    setAadhaarModalIsOpen(true);
  };

  const handleAadhaarVerifySuccess = () => {
    setIsAadhaarVerified(true);
    setShowSuccessAlert(true);
    setError("");
    setAadhaarModalIsOpen(false); 
  };

  const handleModalClose = () => {
    setAadhaarModalIsOpen(false);
  };

  const onClickVerify = async () => {
  if (!isValidAadhaar) {
    setErrorAndScroll({ setError, message: "Invalid Aadhaar number.", topRef });
    return;
  }
  if (!isAadhaarVerified) {
    setErrorAndScroll({ setError, message: "Please verify your Aadhaar number first.", topRef });
    return;
  }
  if (!isValidPhoneNumber) {
    setErrorAndScroll({ setError, message: "Invalid mobile number.", topRef });
    return;
  }
  if (!mobileNumber) {
    setErrorAndScroll({ setError, message: "Mobile number is required.", topRef });
    return;
  }
  if (!aadharNumber) {
    setErrorAndScroll({ setError, message: "Aadhaar number is required.", topRef });
    return;
  }
  if (!password) {
    setErrorAndScroll({ setError, message: "Password is required.", topRef });
    return;
  }
  if (!confirmPassword) {
    setErrorAndScroll({ setError, message: "Confirm password is required.", topRef });
    return;
  }
  if (!isLengthValid || !hasNumber || !hasUpperCase || !hasSpecialChar) {
    setErrorAndScroll({ setError, message: "Password does not meet the required criteria.", topRef });
    return;
  }
  if (password !== confirmPassword) {
    setErrorAndScroll({ setError, message: "Passwords do not match.", topRef });
    return;
  }
  if (!captchaToken) {
    setErrorAndScroll({ setError, message: "Please complete the reCAPTCHA verification.", topRef });
    return;
  }

  try {
    const response = await registerUser(
      aadharNumber?.replace(/ /g, ""),
      mobileNumber,
      password,
      captchaToken
    );
    if (response.message === "User successfully registered!") {
      setActiveStep((prev) => prev + 1);
    } else {
      setErrorAndScroll({
        setError,
        message: response.message || "Registration failed! Please try again.",
        topRef
      });
    }
  } catch (err) {
    console.error("Error in registerUser:", err);
    const errorMessage = typeof err === 'string' ? err : err?.message || "An error occurred while registering. Please try again.";
    setErrorAndScroll({
      setError,
      message: errorMessage,
      topRef
    });
  }
};

  return (
    <>
      <Card
        ref={topRef}
        sx={{ gap: "2rem", maxWidth: "45rem", margin: "auto" }}
        elevation={1}
      >
        <Typography variant="h4">{t("Basic Information")}</Typography>

        <Alert severity="info">
          {t(
            "Please provide your basic details for verification Your Aadhaar will be used for KYC verification"
          )}
        </Alert>

        {error && <Alert severity="error">{error}</Alert>}
        
        {showSuccessAlert && (
          <Alert severity="success">
            {t("Aadhaar number verified successfully!")}
          </Alert>
        )}

        <div className="w-full flex flex-col gap-6">
          <div className="w-full flex flex-col gap-1">
            <Typography variant="body1">{t("Aadhaar Number")}</Typography>
            <div className="flex flex-col gap-2 sm:flex-row">
              <TextField
                value={aadharNumber}
                onChange={(e) =>
                  setAadharNumber(
                    e.target.value.replace(/\D/g, "").replace(/(\d{4})(?=\d)/g, "$1 ")
                  )
                }
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {isAadhaarVerified ? (
                          <CheckCircleRoundedIcon color="primary" />
                        ) : (
                          <BadgeIcon />
                        )}
                      </InputAdornment>
                    )
                  },
                  htmlInput: {
                    maxLength: 14
                  }
                }}
                placeholder={t("XXXX XXXX XXXX")}
                variant="outlined"
                fullWidth
                type="tel"
                disabled={isAadhaarVerified}
              />
              <button 
                className={`text-white hover:cursor-pointer w-half rounded-lg min-w-fit p-1 ${
                  isAadhaarVerified 
                    ? 'bg-gray-500 cursor-not-allowed' 
                    : !isValidAadhaar || aadharNumber.replace(/ /g, "").length !== 12
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
                onClick={handleVerifyAadhaar}
                disabled={isAadhaarVerified || !isValidAadhaar || aadharNumber.replace(/ /g, "").length !== 12}
              >
                {isAadhaarVerified ? t("Verified") : t("Verify Aadhaar")}
              </button>
            </div>
            <Typography color="light-grey" variant="body1">
              {t("Enter your 12digit Aadhaar number")}
            </Typography>
          </div>

          {/* Blur Overlay Container */}
          <div className="relative cursor-not-allowed">
            {/* Fields Container */}
            <div className={`flex flex-col gap-6 ${!isAadhaarVerified ? 'blur-sm' : ''}`}>
              <div className="w-full flex flex-col gap-1">
    <Typography variant="body1">{t("Mobile Number")}</Typography>
    <TextField
      value={mobileNumber}
      onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, ""))}
      placeholder={t("Enter 10digit mobile number")}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <SmartphoneIcon />
            </InputAdornment>
          )
        },
        htmlInput: {
          maxLength: 10
        }
      }}
      variant="outlined"
      fullWidth
      type="tel"
      disabled={!isAadhaarVerified}
    />
  </div>

  <div className="w-full flex flex-col gap-1">
    <Typography variant="body1">{t("Password")}</Typography>
    <TextField
      value={password}
      onChange={(e) => setPassword(e.target.value)}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <LockIcon />
            </InputAdornment>
          )
        }
      }}
      placeholder={t("Create a strong password")}
      variant="outlined"
      type="password"
      fullWidth
      disabled={!isAadhaarVerified}
    />
  </div>

  <div className="w-full flex flex-col gap-1">
    <Typography variant="body1">{t("Confirm Password")}</Typography>
    <TextField
      value={confirmPassword}
      onChange={(e) => setConfirmPassword(e.target.value)}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <LockIcon />
            </InputAdornment>
          )
        }
      }}
      placeholder={t("Confirm your password")}
      variant="outlined"
      type="password"
      fullWidth
      disabled={!isAadhaarVerified}
    />
  </div>

  <Card variant="grey">
    <Typography variant="body1">{t("Password Requirements")}</Typography>
    <Typography color="grey" variant="body1">
      {isLengthValid ? <CheckCircleRoundedIcon color="primary" /> : "❌"} {t("8 characters")}
    </Typography>
    <Typography color="grey" variant="body1">
      {hasLowerCase ? <CheckCircleRoundedIcon color="primary" /> : "❌"} {t("At least one lowercase letter")}
    </Typography>
    <Typography color="grey" variant="body1">
      {hasUpperCase ? <CheckCircleRoundedIcon color="primary" /> : "❌"} {t("At least one uppercase letter")}
    </Typography>
    <Typography color="grey" variant="body1">
      {hasNumber ? <CheckCircleRoundedIcon color="primary" /> : "❌"} {t("At least one number")}
    </Typography>
    <Typography color="grey" variant="body1">
      {hasSpecialChar ? <CheckCircleRoundedIcon color="primary" /> : "❌"} {t("At least one special character")}
    </Typography>
    {confirmPassword && (
      <Typography color="grey" variant="body1">
        {confirmPasswordValid ? (
          <CheckCircleRoundedIcon color="primary" />
        ) : (
          "❌"
        )}{" "}
        {confirmPasswordValid ? t("Passwords match") : t("Passwords do not match")}
      </Typography>
    )}
  </Card>

  {/* reCAPTCHA Section */}
  <div className="flex justify-center">
    <div style={{ pointerEvents: isAadhaarVerified ? "auto" : "none", opacity: isAadhaarVerified ? 1 : 0.6 }}>
      <ReCAPTCHA
        sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
        onChange={handleCaptchaChange}
        onExpired={handleCaptchaExpired}
        theme="light"
      />
    </div>
  </div>

  <Button
    disabled={!isAadhaarVerified || !mobileNumber || !aadharNumber || !password || !confirmPassword}
    variant="contained"
    fullWidth
    endIcon={<ArrowForwardRoundedIcon />}
    onClick={onClickVerify}
  >
    {t("Verify Details")}
  </Button>
            </div>

            {/* Overlay - Only visible when Aadhaar is not verified */}
            {!isAadhaarVerified && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/20 backdrop-blur-[1px] pointer-events-none">
                <div className="flex flex-col items-center gap-3 bg-white/90 px-6 py-4 rounded-lg shadow-lg border border-gray-200 hover:cursor-not-allowed">
                  <Lock className="w-8 h-8 text-gray-600" />
                  <Typography variant="body1" className="text-gray-700 font-medium text-center">
                    {t("Verify Aadhaar First")}
                  </Typography>
                  <Typography variant="body2" className="text-gray-500 text-center max-w-xs">
                    {t("VerifyAadhaarToContinue")}
                  </Typography>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Aadhaar OTP Modal */}
      {aadhaarModalIsOpen && (
        <OTPModal
          aadhar_number={aadharNumber}
          onClose={handleModalClose}
          onVerifySuccess={handleAadhaarVerifySuccess}
        />
      )}
    </>
  );
};

export default BasicInfoRegister;